import { FlaskConical, Lock } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog';
import { useSetTenantMode } from '@/features/settings/api/useSetTenantMode';
import { useTenantMode } from '@/features/settings/api/useTenantMode';
import type { ApiHTTPError } from '@/lib/api/client';
import { toast } from '@/lib/notify';
import { cn } from '@/lib/utils';

// Sidebar Test Mode toggle. ON == Test (test keys + test wallet); OFF == Live.
// Switching to Live is only allowed when the Console has production enabled for
// the company — otherwise the toggle is locked and explains why. Shown only to
// tenant admins (gated by the caller in Sidebar).
export function TestModeToggle({ collapsed }: { collapsed: boolean }) {
  const { data } = useTenantMode();
  const setMode = useSetTenantMode();
  const [confirmLive, setConfirmLive] = useState(false);

  // Until we know the mode, render nothing (avoids a flash of the wrong state).
  if (!data) return null;

  const isTest = data.mode !== 'live';
  const locked = !data.productionEnabled;

  function apply(mode: 'test' | 'live') {
    setMode.mutate(mode, {
      onSuccess: (d) =>
        toast.success(d.mode === 'live' ? 'Switched to Production mode' : 'Switched to Test mode'),
      onError: (e) =>
        toast.error((e as ApiHTTPError).data?.detail ?? 'Could not change mode. Please try again.'),
    });
  }

  function handleToggle() {
    if (setMode.isPending) return;
    if (isTest) {
      // Turning Test Mode OFF → go Live. Blocked until production is enabled.
      if (locked) {
        toast.error('Production is not enabled. Please contact your administrator.');
        return;
      }
      setConfirmLive(true);
    } else {
      // Turning it back ON → Test. Always allowed, no confirmation.
      apply('test');
    }
  }

  const title = locked
    ? 'Test Mode (production not enabled — contact your administrator to go live)'
    : isTest
      ? 'Test Mode is ON — using test keys and test wallet'
      : 'Production mode — using live keys and live wallet';

  return (
    <>
      <button
        type="button"
        onClick={handleToggle}
        disabled={setMode.isPending}
        title={title}
        aria-label={title}
        role="switch"
        aria-checked={isTest}
        className={cn(
          'flex w-full items-center gap-2 rounded-lg border border-transparent px-2 py-[6px] text-sm leading-[150%] text-black-logo transition-all duration-300 hover:border-placeholder disabled:opacity-60',
          collapsed && 'lg:h-[35px] lg:w-[35px] lg:justify-center lg:rounded-full lg:px-0',
        )}
      >
        <FlaskConical className={cn('h-4 w-4 shrink-0', isTest ? 'text-primary' : 'text-gray66')} />

        {!collapsed && (
          <>
            <span className="flex-1 text-start">Test Mode</span>

            {/* LIVE tag when running in production, so the state is unmistakable. */}
            {!isTest && (
              <span className="rounded-full bg-[#FEECEC] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-600">
                Live
              </span>
            )}

            {/* The switch: track + knob. ON (blue) == Test. */}
            <span
              className={cn(
                'relative inline-flex h-4.5 w-8 shrink-0 items-center rounded-full transition-colors duration-200',
                isTest ? 'bg-primary' : 'bg-grayEF',
              )}
            >
              {locked && (
                <Lock className="absolute -top-3.5 end-0 h-3 w-3 text-gray66" aria-hidden />
              )}
              <span
                className={cn(
                  'absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow transition-transform duration-200',
                  isTest ? 'translate-x-4' : 'translate-x-0.5',
                )}
              />
            </span>
          </>
        )}
      </button>

      <ConfirmDialog
        open={confirmLive}
        title="Switch to Production?"
        description="All new session runs will be verified with your live API keys and charged from your Production (live) wallet. Runs already in progress keep the mode they started in."
        confirmLabel="Switch to Production"
        cancelLabel="Stay in Test"
        onConfirm={() => {
          setConfirmLive(false);
          apply('live');
        }}
        onCancel={() => setConfirmLive(false)}
      />
    </>
  );
}
